import React from 'react'
import { ScoutIcon } from 'components/icons/scout'
import { PaperPlaneIcon } from 'components/icons/paper-plane'
import { v4 as uuid } from 'uuid'
import { streamNewsletterContentChatResponse, NewsletterContentChatResponse } from './data'
import '../../template-library/_styles.scss'
import '../suggested/_styles.scss'

interface NewsletterContentChatMessage {
  role: 'user' | 'assistant'
  content: string
  timestamp?: Date
}

interface QuickAction {
  prompt: string
  type: 'direct'
}

interface NewsletterContentChatProps {
  communityId: string
  onError: (error: string) => void
  onSourceCreated?: () => void  // Optional callback when source is created
  sessionId?: string  // Optional session ID to continue an existing chat
  isEditMode?: boolean  // Whether we're in edit mode (affects quick actions)
}

interface NewsletterContentChatState {
  messages: NewsletterContentChatMessage[]
  inputMessage: string
  isLoading: boolean
  isTyping: boolean
  sessionId: string
  showWelcome: boolean
}

export class NewsletterContentChat extends React.Component<NewsletterContentChatProps, NewsletterContentChatState> {
  private chatEndRef: React.RefObject<HTMLDivElement>
  private chatInputRef: React.RefObject<HTMLInputElement>

  constructor(props: NewsletterContentChatProps) {
    super(props)
    this.state = {
      messages: [],
      inputMessage: '',
      isLoading: false,
      isTyping: false,
      sessionId: props.sessionId || uuid(),  // Use provided sessionId or generate new one
      showWelcome: true  // Always show welcome initially
    }
    this.chatEndRef = React.createRef()
    this.chatInputRef = React.createRef()
  }

  componentDidMount() {
    this.scrollToBottom()
  }

  componentDidUpdate(prevProps: NewsletterContentChatProps, prevState: NewsletterContentChatState) {
    // Scroll when messages change or when typing indicator changes
    const messagesChanged = prevState.messages.length !== this.state.messages.length

    let lastMessageChanged = false
    if (this.state.messages.length > 0 && prevState.messages.length > 0) {
      const prevLastMessage = prevState.messages[prevState.messages.length - 1]
      const currentLastMessage = this.state.messages[this.state.messages.length - 1]
      if (prevLastMessage && currentLastMessage) {
        lastMessageChanged = prevLastMessage.content !== currentLastMessage.content
      }
    }

    const typingChanged = prevState.isTyping !== this.state.isTyping

    if (messagesChanged || lastMessageChanged || typingChanged) {
      this.scrollToBottom()
    }
  }

  private scrollToBottom = () => {
    // Immediate scroll for streaming
    if (this.chatEndRef && this.chatEndRef.current) {
      this.chatEndRef.current.scrollTop = this.chatEndRef.current.scrollHeight
    }
    // Also schedule another scroll after a short delay to ensure DOM has updated
    setTimeout(() => {
      if (this.chatEndRef && this.chatEndRef.current) {
        this.chatEndRef.current.scrollTop = this.chatEndRef.current.scrollHeight
      }
    }, 50)
  }

  private handleSendMessage = () => {
    const message = this.state.inputMessage.trim()
    if (!message) return

    // Add user message
    const userMessage: NewsletterContentChatMessage = {
      role: 'user',
      content: message,
      timestamp: new Date()
    }

    this.setState(
      {
        messages: [...this.state.messages, userMessage],
        inputMessage: '',
        isLoading: true,
        isTyping: true,
        showWelcome: false
      },
      () => {
        this.sendToBackend(message)
      }
    )
  }

  private sendToBackend = (message: string) => {
    const payload = {
      message,
      sessionId: this.state.sessionId,
      chatType: 'newsletter-content'
    }

    let assistantMessage = ''

    const callback = (response: NewsletterContentChatResponse | null) => {
      // Stream ended
      if (!response) {
        this.setState({
          isLoading: false,
          isTyping: false
        })
        return
      }

      // Handle error
      if (response.error) {
        this.setState({
          isLoading: false,
          isTyping: false
        })
        this.props.onError(response.error || 'Failed to communicate with Scout. Please try again.')
        return
      }

      // Handle message chunk
      if (response.message) {
        assistantMessage += response.message
        this.setState((prevState) => {
          const messages = [...prevState.messages]
          const lastMessage = messages[messages.length - 1]

          if (lastMessage && lastMessage.role === 'assistant') {
            messages[messages.length - 1] = {
              ...lastMessage,
              content: assistantMessage
            }
          } else {
            messages.push({
              role: 'assistant',
              content: assistantMessage,
              timestamp: new Date()
            })
          }

          return { messages, isTyping: false }
        })
      }

      // Handle completion
      if (response.done) {
        this.setState({
          isLoading: false,
          isTyping: false
        })

        // Check if the response indicates source was created
        if (assistantMessage.toLowerCase().includes('successfully created') ||
            assistantMessage.toLowerCase().includes('source created')) {
          // Notify parent component that source was created
          if (this.props.onSourceCreated) {
            this.props.onSourceCreated()
          }
        }
      }
    }

    streamNewsletterContentChatResponse(this.props.communityId, payload, callback)
  }

  private handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      this.handleSendMessage()
    }
  }

  private renderWelcome = () => {
    // Different quick actions for edit mode vs add mode
    const quickActions: QuickAction[] = this.props.isEditMode ? [
      {
        prompt: 'Update my newsletter summary to be more concise',
        type: 'direct'
      },
      {
        prompt: 'Add more topics to my newsletter content',
        type: 'direct'
      },
      {
        prompt: 'Refine my existing topics to be more specific',
        type: 'direct'
      },
      {
        prompt: 'Change the focus of my newsletter content',
        type: 'direct'
      }
    ] : [
      {
        prompt: 'I want to create a weekly tech newsletter covering AI, software development, and cloud computing',
        type: 'direct'
      },
      {
        prompt: 'Help me create a healthcare newsletter about medical research and wellness tips',
        type: 'direct'
      },
      {
        prompt: 'I need a marketing newsletter focused on digital marketing trends and social media',
        type: 'direct'
      },
      {
        prompt: 'Create a finance newsletter covering stock market news and investment strategies',
        type: 'direct'
      }
    ]

    return (
      <div className="rex-welcome-section">
        <div className="rex-welcome-message">
          <ScoutIcon svgwidth="40" svgheight="40"/>
          <div>
            <h2>Hi there! I'm Scout</h2>
            <p>
              {this.props.isEditMode
                ? "Let's update your newsletter content. Tell me what you'd like to change, and I'll help you refine your content source."
                : "I'll help you define your newsletter content through a conversation. Tell me about your newsletter goals, and I'll help you create a content source with summary and topics. I can even save it for you when you're ready!"
              }
            </p>
          </div>
        </div>

        <div className="rex-quick-actions">
          <h3>{this.props.isEditMode ? 'Quick Updates' : 'Newsletter Ideas'}</h3>
          <div className="rex-action-grid">
            {quickActions.map((action, index) => (
              <button
                key={index}
                className="rex-action-btn"
                onClick={() => {
                  this.setState({ inputMessage: action.prompt }, () => {
                    this.handleSendMessage()
                  })
                }}
              >
                {action.prompt}
              </button>
            ))}
          </div>
        </div>
      </div>
    )
  }

  private formatMessageContent = (content: string) => {
    // Check if message contains SUMMARY and TOPICS (case insensitive)
    const upperContent = content.toUpperCase()
    const hasSummary = upperContent.includes('SUMMARY:') || upperContent.includes('**SUMMARY**:')
    const hasTopics = upperContent.includes('TOPICS:') || upperContent.includes('**TOPICS**:')

    if (!hasSummary && !hasTopics) {
      return <div className="rex-message-text">{content}</div>
    }

    // Parse the content to highlight summary and topics
    const parts = []
    let currentIndex = 0

    // Find SUMMARY section
    const summaryMatch = content.match(/(SUMMARY:|[*]{0,2}Summary[*]{0,2}:)\s*([^\n]+(?:\n(?!TOPICS:|[*]{0,2}Topics[*]{0,2}:)[^\n]+)*)/i)
    if (summaryMatch) {
      const summaryStart = content.indexOf(summaryMatch[0])

      // Add text before summary
      if (summaryStart > 0) {
        parts.push(
          <div key="before-summary" className="rex-message-text">
            {content.substring(0, summaryStart)}
          </div>
        )
      }

      // Add highlighted summary
      parts.push(
        <div key="summary" className="newsletter-summary-section">
          <h4>Summary</h4>
          <p>{summaryMatch[2].trim()}</p>
        </div>
      )

      currentIndex = summaryStart + summaryMatch[0].length
    }

    // Find TOPICS section
    const topicsMatch = content.match(/(TOPICS:|[*]{0,2}Topics[*]{0,2}:)\s*([^]*)/i)
    if (topicsMatch) {
      const topicsStart = content.indexOf(topicsMatch[0], currentIndex)

      // Add text between summary and topics
      if (topicsStart > currentIndex) {
        const betweenText = content.substring(currentIndex, topicsStart).trim()
        if (betweenText) {
          parts.push(
            <div key="between" className="rex-message-text">
              {betweenText}
            </div>
          )
        }
      }

      // Parse topics list and find where the list ends
      const topicsText = topicsMatch[2].trim()
      const lines = topicsText.split('\n')
      const topicLines = []
      let lastTopicIndex = -1

      // Extract topic lines and track where they end
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim()
        if (line.length > 0 && (line.startsWith('-') || line.startsWith('•') || line.match(/^\d+\./))) {
          topicLines.push(line.replace(/^[-•]\s*/, '').replace(/^\d+\.\s*/, ''))
          lastTopicIndex = i
        } else if (topicLines.length > 0 && line.length > 0) {
          // We've hit non-list content after topics, stop here
          break
        }
      }

      if (topicLines.length > 0) {
        parts.push(
          <div key="topics" className="newsletter-topics-section">
            <h4>Topics</h4>
            <ul>
              {topicLines.map((topic, idx) => (
                <li key={idx}>{topic}</li>
              ))}
            </ul>
          </div>
        )
      }

      // Add any text after the topics list (like confirmation questions)
      if (lastTopicIndex >= 0 && lastTopicIndex < lines.length - 1) {
        const afterTopicsText = lines.slice(lastTopicIndex + 1).join('\n').trim()
        if (afterTopicsText) {
          parts.push(
            <div key="after-topics" className="rex-message-text">
              {afterTopicsText}
            </div>
          )
        }
      }
    }

    return <>{parts}</>
  }

  private renderMessage = (message: NewsletterContentChatMessage, index: number) => {
    return (
      <div
        key={index}
        className={`rex-message ${message.role === 'user' ? 'user' : 'assistant'}`}
      >
        <div className="rex-message-avatar">
          {message.role === 'user' ? (
            <i className="fas fa-user" />
          ) : (
            <ScoutIcon svgwidth="24" svgheight="24" />
          )}
        </div>
        <div className="rex-message-content">
          {this.formatMessageContent(message.content)}
          {message.timestamp && (
            <div className="rex-message-timestamp">
              {message.timestamp.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
              })}
            </div>
          )}
        </div>
      </div>
    )
  }

  private renderTypingIndicator = () => {
    return (
      <div className="rex-message assistant">
        <div className="rex-message-avatar">
          <ScoutIcon svgwidth="24" svgheight="24" />
        </div>
        <div className="rex-message-content">
          <div className="rex-typing-indicator">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    )
  }

  render() {
    return (
      <div className="rex-main-content">
        <div className="rex-chat-container" ref={this.chatEndRef}>
          <div className="rex-messages">
            {this.state.showWelcome && this.state.messages.length === 0 && this.renderWelcome()}

            {this.state.messages.map((message, index) => this.renderMessage(message, index))}

            {this.state.isTyping && this.renderTypingIndicator()}
          </div>
        </div>

        <div className="rex-input-container">
          <form className="rex-input-form" onSubmit={(e) => { e.preventDefault(); this.handleSendMessage(); }}>
            <div className="rex-input-wrapper">
              <input
                ref={this.chatInputRef}
                type="text"
                className="rex-message-input"
                placeholder="Tell Scout about your newsletter content needs..."
                value={this.state.inputMessage}
                onChange={(e) => this.setState({ inputMessage: e.target.value })}
                onKeyDown={this.handleKeyDown}
                disabled={this.state.isLoading}
              />
              <button
                type="submit"
                className="rex-send-btn"
                disabled={!this.state.inputMessage.trim() || this.state.isLoading}
              >
                <PaperPlaneIcon />
              </button>
            </div>
          </form>
          <div className="rex-input-footer">
            <small>Scout is powered by AI and may make mistakes. Please verify the generated content.</small>
          </div>
        </div>
      </div>
    )
  }
}
